import { useCallback, useMemo, useState } from 'react';

export const PAYROLL_PERIOD_PRESETS = {
  TODAY: 'today',
  THIS_WEEK: 'thisWeek',
  LAST_WEEK: 'lastWeek',
  THIS_MONTH: 'thisMonth',
  NEXT_MONTH: 'nextMonth',
  LAST_MONTH: 'lastMonth',
  LAST_3_MONTHS: 'last3Months',
  THIS_YEAR: 'thisYear',
  CUSTOM: 'custom',
};

export const PAYROLL_MAX_RANGE_DAYS = 366;

const startOfDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

export const formatDateKey = (date) => {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const parseDateKey = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const getStartOfWeek = (date) => {
  const copy = startOfDay(date);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy;
};

const getEndOfWeek = (date) => {
  const start = getStartOfWeek(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return end;
};

export const resolvePayrollPeriodRange = (preset, customFrom, customTo) => {
  const today = startOfDay(new Date());

  switch (preset) {
    case PAYROLL_PERIOD_PRESETS.TODAY:
      return { from: today, to: today };
    case PAYROLL_PERIOD_PRESETS.THIS_WEEK:
      return { from: getStartOfWeek(today), to: getEndOfWeek(today) };
    case PAYROLL_PERIOD_PRESETS.LAST_WEEK: {
      const currentWeekMonday = getStartOfWeek(today);
      const from = new Date(currentWeekMonday);
      from.setDate(from.getDate() - 7);
      const to = new Date(currentWeekMonday);
      to.setDate(to.getDate() - 1);
      return { from, to };
    }
    case PAYROLL_PERIOD_PRESETS.THIS_MONTH: {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from, to };
    }
    case PAYROLL_PERIOD_PRESETS.NEXT_MONTH: {
      const from = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return { from, to };
    }
    case PAYROLL_PERIOD_PRESETS.LAST_MONTH: {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from, to };
    }
    case PAYROLL_PERIOD_PRESETS.LAST_3_MONTHS: {
      const from = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from, to };
    }
    case PAYROLL_PERIOD_PRESETS.THIS_YEAR: {
      const from = new Date(today.getFullYear(), 0, 1);
      return { from, to: today };
    }
    case PAYROLL_PERIOD_PRESETS.CUSTOM:
      return {
        from: customFrom ? startOfDay(customFrom) : null,
        to: customTo ? startOfDay(customTo) : null,
      };
    default:
      return { from: today, to: today };
  }
};

export const getPayrollRangeDayCount = (from, to) => {
  if (!from || !to) return 0;
  const start = startOfDay(from);
  const end = startOfDay(to);
  const diffMs = end.getTime() - start.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
};

const usePayrollReferencePeriod = () => {
  const [periodPreset, setPeriodPreset] = useState(PAYROLL_PERIOD_PRESETS.THIS_MONTH);
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [customRangeTouched, setCustomRangeTouched] = useState(false);

  const resolvedRange = useMemo(
    () => resolvePayrollPeriodRange(periodPreset, customFrom, customTo),
    [periodPreset, customFrom, customTo],
  );

  const dateRange = useMemo(() => {
    if (!resolvedRange.from || !resolvedRange.to) {
      return null;
    }
    return {
      from: formatDateKey(resolvedRange.from),
      to: formatDateKey(resolvedRange.to),
    };
  }, [resolvedRange]);

  const rangeDayCount = useMemo(
    () => getPayrollRangeDayCount(resolvedRange.from, resolvedRange.to),
    [resolvedRange],
  );

  const isRangeValid = useMemo(() => {
    if (periodPreset !== PAYROLL_PERIOD_PRESETS.CUSTOM) {
      return Boolean(dateRange);
    }
    if (!resolvedRange.from || !resolvedRange.to) {
      return false;
    }
    if (resolvedRange.from > resolvedRange.to) {
      return false;
    }
    return rangeDayCount <= PAYROLL_MAX_RANGE_DAYS;
  }, [periodPreset, dateRange, resolvedRange, rangeDayCount]);

  const rangeErrorKey = useMemo(() => {
    if (periodPreset !== PAYROLL_PERIOD_PRESETS.CUSTOM) {
      return null;
    }
    if (!resolvedRange.from || !resolvedRange.to) {
      return 'employeePayments.payroll.error.customRangeRequired';
    }
    if (resolvedRange.from > resolvedRange.to) {
      return 'employeePayments.payroll.error.customRangeOrder';
    }
    if (rangeDayCount > PAYROLL_MAX_RANGE_DAYS) {
      return 'employeePayments.payroll.error.customRangeMax';
    }
    return null;
  }, [periodPreset, resolvedRange, rangeDayCount]);

  const getSubmitRangeErrorKey = useCallback(() => rangeErrorKey, [rangeErrorKey]);

  const handlePresetChange = useCallback((value) => {
    setPeriodPreset(value);
    if (value !== PAYROLL_PERIOD_PRESETS.CUSTOM) {
      setCustomRangeTouched(false);
    }
  }, []);

  const touchCustomRange = useCallback(() => {
    setCustomRangeTouched(true);
  }, []);

  const visibleRangeErrorKey = customRangeTouched ? rangeErrorKey : null;

  return {
    periodPreset,
    customFrom,
    customTo,
    setCustomFrom,
    setCustomTo,
    dateRange,
    isRangeValid,
    rangeErrorKey,
    visibleRangeErrorKey,
    touchCustomRange,
    getSubmitRangeErrorKey,
    handlePresetChange,
  };
};

export default usePayrollReferencePeriod;
